import { FC, useEffect, useMemo, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmpty, beautifyTheLowHighObject } from '@lhb/func';
import { getSimilarBusinessCircleList, getShareSimilarBusinessCircleList } from '@/common/api/youth';
import { getSimilarMetric, shouldShowSimilarRevenue, shouldShowSimilarMetric, getSimilarLevelImage, getSimilarityText } from './similarBusinessCircle';
import styles from './index.module.less';
import V2Title from '@/common/components/Feedback/V2Title';
import IconFont from '@/common/components/IconFont';
import AreaDetailDrawer from '@/common/components/business/YouthMap/AreaDetailDrawer';

const SimilarBusinessCircle: FC<{
  modelClusterId?: number;
  modelConfigId?: number | string;
  configSwitch?: any;
  detailLevel?: number;
  isShare?: boolean; // 分享页场景：不允许点击卡片打开二层商圈详情
  tenantId?: number | string; // 分享场景租户id（仅点位测评-商场店分享时传入，用于区分来源并调用分享接口）
  userId?: number | string; // 分享场景用户id（仅点位测评-商场店分享时传入）
}> = ({ modelClusterId, modelConfigId, configSwitch, detailLevel = 0, isShare = false, tenantId, userId }) => {
  const [list, setList] = useState<any[]>([]);
  const [similarDrawerData, setSimilarDrawerData] = useState<any>({
    open: false,
    id: '',
    rankVal: null,
  });
  const methods = useMethods({
    loadList() {
      if (!modelClusterId || !modelConfigId) {
        setList([]);
        return;
      }
      // 分享接口仅在「点位测评-商场店」分享场景下调用：isShare 且传入了 tenantId/userId；
      // 青春版分享不传这两项，仍走普通接口
      const isShareList = isShare && isNotEmpty(tenantId) && isNotEmpty(userId);
      const params: any = {
        modelClusterId,
        modelConfigId,
      };
      if (isShareList) {
        params.tenantId = tenantId;
        params.userId = userId;
      }
      const targetFetch = isShareList ? getShareSimilarBusinessCircleList : getSimilarBusinessCircleList;
      targetFetch(params).then((res) => {
        const data = isArray(res) ? res : res?.data;
        setList(isArray(data) ? data.slice(0, 3) : []);
      }, () => {
        setList([]);
      });
    },
    openItem(event: any) {
      const id = Number(event?.currentTarget?.dataset?.id);
      // 分享页或已处于二层详情时，不允许再打开下一层商圈详情
      if (isShare || detailLevel >= 1 || !id) return;
      setSimilarDrawerData({
        open: true,
        id,
        rankVal: null,
      });
    },
  });

  useEffect(() => {
    methods.loadList();
  }, [modelClusterId, modelConfigId]);

  const cards = useMemo(() => list.map((item) => {
    const metric = getSimilarMetric(item, configSwitch);
    return {
      ...item,
      // 接口返回的 projectLevel 是「S级」这类中文，命中映射才展示等级图标；未返回或无对应图标则不展示
      levelImage: getSimilarLevelImage(item.projectLevel),
      // 相似度标签，接口不返回时不展示
      similarityText: getSimilarityText(item.similarity),
      metric,
      revenue: shouldShowSimilarRevenue(item) ? beautifyTheLowHighObject(1, item.salesAmountPredict, 1, { low: '元/年', high: '万元/年', top: '亿元/年' }) : null,
      metricValue: beautifyTheLowHighObject(1, metric.value, 1, { low: metric.unit, high: `万${metric.unit}`, top: `亿${metric.unit}` }),
    };
  }), [list, configSwitch]);

  // 分享页或已处于二层详情时，禁止点击卡片打开下一层商圈详情
  const disabledOpen = isShare || detailLevel >= 1;

  if (!cards.length) return null;

  return <>
    <section className={styles.similarBusinessCircle}>
      {/* 标题与「商场客流」保持一致，统一使用 V2Title */}
      <V2Title text='相似商圈' divider className={styles.headerTitle} bold/>
      <div className={`${styles.cardList} ${styles[`count${cards.length}`]}`}>
        {cards.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className={`${styles.card} ${disabledOpen ? styles.disabled : ''}`}
            data-id={item.id}
            onClick={disabledOpen ? undefined : methods.openItem}
          >
            <div className={styles.cardBody}>
              <div className={styles.head}>
                {item.name || '-'}
              </div>
              <div className={styles.middleModule}>
                {/* 等级图标、商圈类型、相似度均无数据时不渲染整行标签 */}
                {(!!item.levelImage || !!item.clusterType || !!item.similarityText) && (
                  <div className={styles.tags}>
                    {!!item.levelImage && (
                      <img className={styles.levelImage} src={item.levelImage} alt={item.projectLevel || ''} />
                    )}
                    {!!item.clusterType && <div>{item.clusterType}</div>}
                    {!!item.similarityText && <div>{item.similarityText}</div>}
                  </div>
                )}
                <div className={styles.address}>
                  <IconFont iconHref='icon-ui-lib-dingwei1' style={{ marginRight: '2px' }}/>
                  {item.address || '-'}
                </div>
              </div>
              <div className={styles.metrics}>
                {!!item.revenue?.val && (
                  <div className={styles.metricWrapper}>
                    <div className={styles.metric}>
                      <div className={styles.metricModule1}>
                        <div className={styles.metricLabel}>营业额</div>
                      </div>
                      <div className={styles.metricModule2}>
                        <div className={styles.metricVal}>{item.revenue?.val}</div>
                        <div className={styles.metricUnit}>{item.revenue?.unit}</div>
                      </div>
                    </div>
                  </div>
                )}
                {/* 显隐规则与移动端共用 shouldShowSimilarMetric：车辆服务兜底 0 仍展示，其余指标无数据不展示 */}
                {shouldShowSimilarMetric(item.metric) && (
                  <div className={styles.metricWrapper}>
                    <div className={styles.metric}>
                      <div className={styles.metricModule1}>
                        <div className={styles.metricLabel}>{item.metric.label}</div>
                      </div>
                      <div className={styles.metricModule2}>
                        <div className={styles.metricVal}>{item.metricValue?.val}</div>
                        <div className={styles.metricUnit}>{item.metricValue?.unit}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.marketScore}>{isNotEmpty(item.marketScore) ? item.marketScore : '-'}</div>
          </div>
        ))}
      </div>
    </section>
    {!disabledOpen && <AreaDetailDrawer
      modelConfigId={modelConfigId}
      drawerData={similarDrawerData}
      setDrawerData={setSimilarDrawerData}
      showCollect={false}
      showTaskCreate={false}
      showTaskHistory={false}
      showGatherCreate={false}
      hideTopActions
      viewChanceDetail={false}
      detailLevel={1}
    />}
  </>;
};

export default SimilarBusinessCircle;
